import React, { useEffect, useState } from 'react';
import { CheckCircle, ExternalLink, ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PlatformCard = ({ title, description, features, icon, color }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`relative bg-gray-900 rounded-xl border border-gray-800 p-6 transition-all duration-500 overflow-hidden ${isHovered ? 'transform-gpu scale-102' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glowing background effect */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-500 ${isHovered ? 'opacity-10' : ''}`} 
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}, transparent 70%)` }}
      />
      
      {/* Card content */}
      <div className="relative z-10">
        <div className={`flex items-center mb-4 h-16 transition-all duration-300 ${isHovered ? 'scale-110' : ''}`}>
          {icon}
        </div>
        
        <div className="flex items-center mb-3">
          <h3 className="text-2xl font-bold text-white">{title}</h3>
          <div className={`ml-2 h-1 w-5 rounded-full transition-all duration-300 ${isHovered ? 'w-12' : ''}`} style={{ backgroundColor: color }}></div>
        </div>
        
        <p className="text-gray-400 mb-6">{description}</p>
        
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start group">
              <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0 transition-colors duration-300" style={{ color }} />
              <span className="text-gray-300 group-hover:text-white transition-colors duration-200">{feature}</span>
            </li>
          ))}
        </ul>
        
        {/* <div className={`mt-6 overflow-hidden transition-all duration-500 ${isHovered ? 'h-10 opacity-100' : 'h-0 opacity-0'}`}>
          <button 
            className="flex items-center text-sm font-medium transition-colors duration-300"
            style={{ color }}
          >
            Learn more about our {title} solutions
            <ChevronRight className="ml-1 w-4 h-4" />
          </button>
        </div> */}
      </div>
    </div>
  );
};

const TabButton = ({ active, title, onClick, color }) => {
  return (
    <button 
      className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${active ? 'text-white' : 'text-gray-400 hover:text-gray-200'}`}
      onClick={onClick}
    >
      {title}
      {active && (
        <span 
          className="absolute bottom-0 left-0 h-0.5 w-full rounded-full transition-all duration-300" 
          style={{ backgroundColor: color }}
        />
      )}
    </button>
  );
};

const DevOpsPlatforms = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const [activeCategory, setActiveCategory] = useState('all');
  
  const platforms = [
    {
      title: "AWS",
      category: "cloud",
      description: "Leverage the power and flexibility of Amazon Web Services with our advanced infrastructure automation.",
      color: "#FF9900",
      icon: (
        <svg viewBox="0 0 24 24" className="h-14 w-14 text-orange-500" fill="currentColor">
          <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.503.335a.383.383 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.295.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.67-.367-1.26-.367-.271 0-.541.04-.811.12a5.98 5.98 0 0 0-.787.24c-.128.064-.223.096-.295.096-.08 0-.151-.032-.223-.096a.978.978 0 0 1-.12-.263L1.6 6.921c-.048-.207-.072-.335-.072-.399 0-.08.024-.16.072-.223.048-.064.144-.12.288-.176.431-.185.87-.295 1.306-.383.446-.08.871-.12 1.282-.12 1.045 0 1.795.207 2.258.622.455.416.686 1.046.686 1.891v2.503h.001zm-3.234 1.038c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.223-.327.27-.535.048-.207.08-.455.08-.734V9.33a7.03 7.03 0 0 0-.735-.136 5.36 5.36 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.662.285.87.19.208.47.311.85.311zm6.41-5.37c0-.08.032-.16.088-.239.064-.08.16-.119.296-.119h.87c.128 0 .215.032.27.08.056.048.088.112.12.2v8.184c0 .08-.032.16-.088.24-.064.08-.16.119-.296.119h-.83c-.128 0-.215-.04-.27-.08-.056-.048-.096-.12-.12-.2v-8.183zm8.95 7.95c.056.064.088.16.088.28 0 .08-.032.16-.088.24-.064.08-.16.119-.296.119h-.942c-.165 0-.275-.04-.335-.112a.799.799 0 0 1-.144-.24l-.39-.934c-.486.734-1.157 1.302-1.915 1.357-.758.056-1.453-.303-2.035-.87a4.575 4.575 0 0 1-.629-.734 2.323 2.323 0 0 1-.391-.83 3.136 3.136 0 0 1-.128-.95v-5.453c0-.08.032-.16.088-.239.064-.08.16-.119.296-.119h.87c.128 0 .215.032.27.08.056.048.096.12.12.2v5.086c0 .32.056.607.168.87.112.263.287.47.51.63.223.16.519.255.886.255.367 0 .67-.095.918-.263.247-.175.43-.375.55-.615.119-.24.19-.494.198-.765v-5.198c0-.08.032-.16.088-.239.064-.08.16-.119.296-.119h.87c.128 0 .215.032.27.08.056.048.096.12.12.2v7.143l.255.446zm-26.305-.24c.045-.074.105-.118.177-.131.07-.014.14-.003.21.034.225.119.46.214.695.283.242.07.51.106.802.106.343 0 .595-.063.758-.191.163-.127.242-.303.242-.526 0-.23-.08-.415-.241-.556-.16-.141-.447-.277-.857-.4a7.621 7.621 0 0 1-1.143-.398 3.217 3.217 0 0 1-.857-.534 2.129 2.129 0 0 1-.53-.71 2.267 2.267 0 0 1-.185-.948c0-.4.086-.764.258-1.093.172-.328.404-.6.694-.819.29-.219.623-.388.997-.505.374-.118.765-.177 1.175-.177.261 0 .524.019.787.056.263.037.517.09.76.16.244.07.475.154.695.252.22.098.412.204.573.319.126.096.21.2.25.313.04.112.045.23.016.354l-.29.841a.335.335 0 0 1-.18.228c-.076.045-.164.05-.263.018a4.443 4.443 0 0 0-.637-.232 2.807 2.807 0 0 0-.737-.106c-.275 0-.501.052-.678.16-.177.106-.266.282-.266.526 0 .118.03.217.089.3.059.081.147.153.266.213.119.06.27.121.452.184.183.062.4.13.652.204.468.137.864.29 1.191.46.326.17.59.359.793.566.203.208.35.437.443.688.092.251.138.532.138.841 0 .681-.264 1.242-.792 1.682-.527.44-1.285.66-2.272.66-.447 0-.854-.047-1.221-.142a4.16 4.16 0 0 1-.94-.351 3.292 3.292 0 0 1-.638-.428c-.161-.142-.27-.27-.33-.382-.062-.118-.087-.233-.075-.347.012-.113.05-.21.114-.292l.331-.584zm9.55-3.259c0-.458-.07-.866-.21-1.224a2.628 2.628 0 0 0-.582-.906 2.472 2.472 0 0 0-.875-.564c-.34-.127-.71-.191-1.115-.191-.403 0-.774.064-1.113.191-.34.127-.636.314-.89.564-.254.25-.446.555-.578.906-.131.35-.196.766-.196 1.224 0 .459.065.876.196 1.253.132.376.324.694.578.956.254.261.549.458.89.592.34.134.71.2 1.113.2.404 0 .775-.066 1.115-.2.34-.134.634-.33.875-.592.24-.262.427-.58.582-.956.14-.378.21-.794.21-1.253zm-3.934 0c0-.53.106-.947.318-1.253.212-.305.496-.458.853-.458.357 0 .642.153.854.458.213.306.319.724.319 1.253 0 .53-.106.947-.319 1.253-.212.305-.497.458-.854.458-.357 0-.641-.153-.853-.458-.212-.306-.318-.724-.318-1.253zm-2.835 2.202c-.11.118-.243.177-.4.177h-.922c-.11 0-.212-.026-.303-.08-.092-.053-.155-.134-.188-.24l-1.122-3.11-.355-.988-.306.873-1.212 3.225c-.034.106-.09.187-.17.24a.403.403 0 0 1-.272.08h-.901c-.156 0-.291-.059-.4-.177-.11-.118-.16-.241-.153-.372l.016-.12 2.21-5.714c.033-.107.09-.187.17-.24a.403.403 0 0 1 .272-.08h1.029c.11 0 .211.026.302.08.09.053.153.133.188.24l2.178 5.714.016.12c.016.131-.03.254-.129.372zm9.885-2.18c0-.444-.065-.843-.194-1.197a2.515 2.515 0 0 0-.557-.898 2.392 2.392 0 0 0-.85-.564c-.336-.13-.71-.194-1.123-.194-.259 0-.5.033-.721.1-.222.067-.425.16-.61.28-.185.12-.347.259-.485.416-.138.157-.244.331-.316.521l4.856.001v1.535zm-1.267 2.196c-.12.185-.287.342-.5.47-.213.128-.457.231-.732.31-.275.077-.566.116-.873.116-.406 0-.778-.066-1.115-.199a2.43 2.43 0 0 1-.884-.577 2.587 2.587 0 0 1-.585-.906c-.139-.36-.208-.76-.208-1.2v-.093c0-.49.078-.931.235-1.325.157-.394.377-.732.66-1.015.282-.283.623-.5 1.02-.651.4-.15.838-.225 1.316-.225.497 0 .945.075 1.343.225.399.15.74.367 1.02.652.281.283.497.632.648 1.045.15.413.225.885.225 1.412v.342c0 .156-.032.277-.097.364-.065.086-.153.13-.266.13H11.89c.024.176.07.338.14.485.07.147.164.275.281.386.118.11.259.198.425.263.165.065.355.097.571.097.254 0 .473-.036.655-.107.181-.072.334-.15.456-.236a.894.894 0 0 1 .266-.178.566.566 0 0 1 .228-.052c.073 0 .138.016.197.048.059.032.103.075.132.128l.363.606a.23.23 0 0 1 .031.145c0 .076-.049.154-.147.234z"/>
        </svg>
      ),
      features: [
        "Multi-Region Infrastructure as Code",
        "Containerized Microservices Orchestration",
        "Automated Compliance & Security Scanning",
        "Custom CloudWatch Monitoring & Alerts"
      ]
    },
    {
      title: "GCP",
      category: "cloud",
      description: "Utilize Google Cloud Platform's innovative solutions with our cloud-native implementation expertise.",
      color: "#4285F4",
      icon: (
        <svg viewBox="0 0 24 24" className="h-14 w-14 text-blue-500" fill="currentColor">
          <path d="M12 0c-.372 0-.74.071-1.091.214l-7.08 2.874a1.93 1.93 0 0 0-1.213 1.77v14.217c0 .827.514 1.57 1.289 1.854l7.08 2.874a2.164 2.164 0 0 0 1.611.03l7.08-2.874a1.93 1.93 0 0 0 1.289-1.854V4.858a1.93 1.93 0 0 0-1.289-1.854L13.182.214A2.164 2.164 0 0 0 12 0zm.258 3.667l5.333 2.995v.744l-5.333-2.995v-.744zm0 2.011l5.333 2.995v5.326l-5.333-2.995V5.678zm-5.914 3.324l5.333 2.995v5.326l-5.333-2.995V9.002z"/>
        </svg>
      ),
      features: [
        "Anthos Multi-Cloud Management",
        "Cloud Run & Cloud Functions Integration",
        "Advanced Data Processing with Dataflow",
        "Zero-Trust Network Security Implementation"
      ]
    },
    {
      title: "Azure",
      category: "cloud",
      description: "Harness Microsoft Azure's enterprise-grade cloud services with our expert implementation team.",
      color: "#0078D4",
      icon: (
        <svg viewBox="0 0 24 24" className="h-14 w-14 text-blue-600" fill="currentColor">
          <path d="M5.483 21.3H24L14.025 4.013l-3.038 8.347 5.836 6.938L5.483 21.3zM13.23 2.7L6.105 8.677 0 19.253h5.505L13.23 2.7z"/>
        </svg>
      ),
      features: [
        "Enterprise-Scale Landing Zone Architecture",
        "AKS with Advanced Networking & Security",
        "CI/CD with Azure DevOps & GitHub Actions",
        "Infrastructure Management with Bicep & Terraform"
      ]
    },
    {
      title: "On-Premises",
      category: "infra",
      description: "Transform your on-premises infrastructure with our modern DevOps practices and automation expertise.",
      color: "#6E6E6E",
      icon: (
        <svg viewBox="0 0 24 24" className="h-14 w-14 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
          <rect x="9" y="9" width="6" height="6"></rect>
          <line x1="9" y1="2" x2="9" y2="4"></line>
          <line x1="15" y1="2" x2="15" y2="4"></line>
          <line x1="9" y1="20" x2="9" y2="22"></line>
          <line x1="15" y1="20" x2="15" y2="22"></line>
          <line x1="20" y1="9" x2="22" y2="9"></line>
          <line x1="20" y1="15" x2="22" y2="15"></line>
          <line x1="2" y1="9" x2="4" y2="9"></line>
          <line x1="2" y1="15" x2="4" y2="15"></line>
        </svg>
      ),
      features: [
        "Private Cloud Kubernetes Implementation",
        "GitOps-based Configuration Management",
        "Legacy Systems Modernization & Integration",
        "Hybrid Connectivity Solutions"
      ]
    }
  ];

  const filteredPlatforms = activeCategory === 'all' 
    ? platforms 
    : platforms.filter(platform => platform.category === activeCategory);

  return (
    <div>
        <Header />
    <section className="bg-gray-950 py-20 overflow-hidden relative">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20" style={{ 
        background: 'radial-gradient(circle at 20% 20%, rgba(66, 133, 244, 0.15) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255, 153, 0, 0.1) 0%, transparent 40%)' 
      }} />
      
      <div className="absolute w-96 h-96 -top-48 -right-48 rounded-full bg-blue-500 opacity-5 blur-3xl"></div>
      <div className="absolute w-96 h-96 -bottom-48 -left-48 rounded-full bg-purple-500 opacity-5 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-gray-800 text-gray-400 text-xs font-medium mb-4">
            INFRASTRUCTURE EXPERTISE
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-4 text-center">
            Optimize Your Infrastructure <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Anywhere</span>
          </h2>
          
          <p className="text-xl text-gray-400 max-w-3xl mx-auto text-center">
            We deliver high-performance DevOps solutions across all major cloud platforms 
            and on-premises environments with enterprise-grade security and scalability.
          </p>
          
          <div className="mt-8 flex space-x-2 bg-gray-800 p-1 rounded-lg">
            <TabButton 
              active={activeCategory === 'all'} 
              title="All Platforms" 
              onClick={() => setActiveCategory('all')}
              color="#6366F1"
            />
            <TabButton 
              active={activeCategory === 'cloud'} 
              title="Cloud" 
              onClick={() => setActiveCategory('cloud')}
              color="#6366F1"
            />
            <TabButton 
              active={activeCategory === 'infra'} 
              title="On-Premises" 
              onClick={() => setActiveCategory('infra')}
              color="#6366F1"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredPlatforms.map((platform, index) => (
            <PlatformCard
              key={index}
              title={platform.title}
              description={platform.description}
              features={platform.features}
              icon={platform.icon}
              color={platform.color}
            />
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="p-px rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 inline-block">
            <Link to='/contact'>
            <button className="bg-gray-900 hover:bg-gray-800 text-white font-medium py-3 px-8 rounded-lg transition-all duration-300 flex items-center">
              <span>Schedule a Technical Assessment</span>
              <ExternalLink className="ml-2 w-4 h-4" />
            </button>
            </Link>
          </div>
          
          <p className="text-gray-500 mt-4 text-sm">
            No commitment required • Full infrastructure evaluation • Custom roadmap
          </p>
        </div>
      </div>
    </section>
    <Footer/>
    </div>
  );
};

export default DevOpsPlatforms;